/**
 * AI assistant chat — shared types for the data/socket layer and the UI.
 *
 * Text chat + composer mic dictation (STT into the input, user Send). Voice-agent
 * continuous talk (`auto_submit: true`) is deferred. See
 * docs/ai-gateway-frontend-integration.md and docs/ai-gateway-integration-guide.md.
 */

// ── UI-facing chat model (what the drawer/composer render) ──────────────────

export type ChatRole = "user" | "assistant";

/** A turn's lifecycle for one assistant message in the UI. */
export type ChatMessageStatus = "streaming" | "complete" | "incomplete";

export interface ChatMessage {
  /** Server `message_id` once known; a temporary local id before then. */
  id: string;
  role: ChatRole;
  content: string;
  /** Only meaningful for assistant messages mid/most-stream. */
  status?: ChatMessageStatus;
  /** ISO timestamp (server `created_at`, or local time for optimistic rows). */
  createdAt?: string;
}

export interface ChatConversation {
  id: string;
  /** Nullable on the backend until a title is PATCHed; coalesce in the UI. */
  title: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Composer states. `text` and `dictation` are live; `connecting`/`voice` are
 * reserved for the deferred voice-agent product (UI-only — never `auto_submit`).
 */
export type ComposerMode = "text" | "dictation" | "connecting" | "voice";

/** The whole-turn status surfaced by the hook (gates the send button). */
export type TurnStatus = "idle" | "streaming" | "cancelling";

/** A transient "working" chip while a server tool runs. */
export interface ToolActivity {
  tool: string;
  label: string;
}

// ── Backend REST payloads (raw shapes from ai-gateway) ──────────────────────

/** Platform list envelope. `next` is reserved (always null today). */
export interface Page<T> {
  results: T[];
  next: string | null;
}

export interface ApiConversation {
  id: string;
  scope: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiMessage {
  id: string;
  // enum also has "system" (never persisted) — keep it permissive.
  role: "user" | "assistant" | "system";
  modality: "text" | "voice";
  content: string;
  created_at: string;
}

// ── WebSocket frame protocol (proxied via /api/ai-gateway/stream) ────────────

/** Client → server frames (text turn + composer dictation). */
export type ClientFrame =
  | { type: "user_message"; content: string }
  | { type: "cancel" }
  | {
      type: "audio_start";
      sample_rate: number;
      encoding: "linear16";
      /** Composer dictation must omit or set false. Voice-agent later uses true. */
      auto_submit?: boolean;
    }
  | { type: "audio_end" };

/**
 * Server → client frames. A normal text turn is:
 *   message_start → (text_delta | tool_activity)* → message_complete → done
 * Dictation (no AI turn): transcript_interim* → transcript_final
 * Terminal types for a turn: done | cancelled | error | reauth_required.
 * Session stream stays open after turn terminals (unlike the old per-turn proxy).
 */
export type ServerFrame =
  | { type: "message_start"; message_id: string; event_id?: string }
  | { type: "text_delta"; text: string; event_id?: string }
  | {
      type: "tool_activity";
      tool: string;
      label: string;
      status: "running" | "ok" | "error";
      event_id?: string;
    }
  | {
      type: "message_complete";
      message_id: string;
      content: string;
      incomplete?: boolean;
      event_id?: string;
    }
  | { type: "done"; event_id?: string }
  | { type: "cancelled"; message: string; event_id?: string }
  | {
      type: "error";
      error: string;
      message: string;
      details?: unknown;
      event_id?: string;
    }
  | { type: "reauth_required"; message: string; event_id?: string }
  | { type: "transcript_interim"; text: string; event_id?: string }
  | { type: "transcript_final"; text: string; event_id?: string }
  | { type: "conversation_titled"; title: string; event_id?: string };

/** WS error codes the gateway emits as `error` frames (key on these, not text). */
export const NON_TERMINAL_WS_ERRORS = ["rate_limited", "conflict"] as const;
export type WsErrorCode = string;

/** True for `rate_limited`/`conflict` — surface a toast, keep the prompt, no re-login. */
export function isNonFatalWsError(code: WsErrorCode): boolean {
  return (NON_TERMINAL_WS_ERRORS as readonly string[]).includes(code);
}
